import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/add';
import SuitcaseIcon from '@atlaskit/icon/core/briefcase';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';

const agentAboutPanelStyles = xcss({
	boxShadow: 'elevation.shadow.raised',
	borderRadius: '6px',
	padding: 'space.300',
	display: 'flex',
	flexDirection: 'column',
});

const headingStyles = xcss({
	textTransform: 'uppercase',
});

const AbouSectionHeaders = ({ children }: { children: React.ReactNode }) => (
	<Box xcss={headingStyles}>
		<Text size="medium" color="color.text.subtle" weight="semibold">
			{children}
		</Text>
	</Box>
);

export const AgentAboutPanelWrapper = ({ children }: { children: React.ReactNode }) => (
	<Stack xcss={agentAboutPanelStyles} space="space.300">
		{children}
	</Stack>
);

export const AgentAboutPanel = () => {
	const { formatMessage } = useIntl();
	return (
		<AgentAboutPanelWrapper>
			<Stack space="space.100">
				<AbouSectionHeaders>{formatMessage(messages.aboutLabel)}</AbouSectionHeaders>

				<Inline space="space.100" alignBlock="center">
					<SuitcaseIcon label="" color="currentColor" />
					{formatMessage(messages.rovoAgentLabel)}
				</Inline>
			</Stack>
			<Stack space="space.100">
				<AbouSectionHeaders>{formatMessage(messages.knowledgeLabel)}</AbouSectionHeaders>

				<Inline space="space.100" alignBlock="center">
					{formatMessage(messages.emptyKnowledgeLabel)}
				</Inline>
			</Stack>
			<Stack space="space.100">
				<AbouSectionHeaders>{formatMessage(messages.teamsLabel)}</AbouSectionHeaders>

				<Inline space="space.100" alignBlock="center">
					<IconButton icon={AddIcon} label="add" />

					{formatMessage(messages.createTeamLabel)}
				</Inline>
			</Stack>
		</AgentAboutPanelWrapper>
	);
};

const messages = defineMessages({
	aboutLabel: {
		id: 'ptc-directory.agent-profile.about-agent.about-label',
		defaultMessage: 'About',
		description: 'Label for about section of the agent',
	},
	rovoAgentLabel: {
		id: 'ptc-directory.agent-profile.about-agent.rovo-agent-label',
		defaultMessage: 'Rovo Agent',
		description: 'The content inside About section, indicating this profile is a Rovo Agent',
	},
	knowledgeLabel: {
		id: 'ptc-directory.agent-profile.about-agent.knowledge-label',
		defaultMessage: 'Knowledge',
		description: 'Label for knowledge section of the agent',
	},
	emptyKnowledgeLabel: {
		id: 'ptc-directory.agent-profile.about-agent.empty-knowledge-label',
		defaultMessage: 'No knowledge added',
		description: 'Text for knowledge section of the agent when it is empty',
	},
	teamsLabel: {
		id: 'ptc-directory.agent-profile.about-agent.teams-label',
		defaultMessage: 'Teams',
		description: 'Label for teams section of the agent',
	},
	createTeamLabel: {
		id: 'ptc-directory.agent-profile.about-agent.teams-label',
		defaultMessage: 'Create a team together',
		description: 'Label for teams section of the agent',
	},
});
